import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ShoppingCart, Star, ArrowLeft, Filter, Search, Globe, Home, GraduationCap, Trophy, ShoppingBag, Code } from 'lucide-react';
import { apiService } from '../services/api';
import { useCart } from '../contexts/CartContext';

interface Product {
  id: string;
  name: string;
  price: string;
  priceAmount?: number | null; // centimes, source de vérité pour la vente
  quantity?: number;           // stock disponible
  category: string;
  stock: boolean;
  available: boolean;
  popular: boolean;
  description: string;
  imageUrl?: string;
}

const BoutiquePage = () => {
  const { add, count } = useCart();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hoveredProduct, setHoveredProduct] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('Tous');
  const [currentYear] = useState(new Date().getFullYear());
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<string[]>(['Tous']);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await apiService.getProducts();
      if (response.success && response.data && Array.isArray(response.data)) {
        // Vitrine : on affiche tout le catalogue publié. La disponibilité et le
        // stock sont montrés en badge (« Rupture ») et conditionnent le bouton
        // d'achat — ils ne cachent pas le produit. L'ancien filtre
        // `available && stock` masquait tout le catalogue (tous les produits
        // ont available=false en attendant l'e-commerce) et rendait mort le
        // code d'affichage des ruptures.
        const productsData = response.data.filter(
          (product: any) => product.published !== false
        ) as Product[];
        setProducts(productsData);
        
        // Extraire les catégories uniques
        const uniqueCategories: string[] = ['Tous', ...new Set(productsData.map((p: Product) => p.category))];
        setCategories(uniqueCategories);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des produits:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigation = [
    { path: '/', label: 'Accueil', icon: Home },
    { path: '/formations', label: 'Formations', icon: GraduationCap },
    { path: '/competitions', label: 'Compétitions', icon: Trophy },
    { path: '/projets', label: 'Projets', icon: Code },
    { path: '/boutique', label: 'Boutique', icon: ShoppingBag }
  ];

  const filteredProducts = selectedCategory === 'Tous'
    ? products
    : products.filter(product => product.category === selectedCategory);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Bouton panier flottant — visible dès qu'un article y est ajouté */}
      {count > 0 && (
        <Link
          to="/panier"
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-5 py-3 rounded-full bg-red-600 hover:bg-red-700 text-white shadow-xl shadow-red-600/30 transition-all hover:scale-105"
          aria-label={`Voir le panier (${count} article${count > 1 ? 's' : ''})`}
        >
          <ShoppingCart className="w-5 h-5" />
          <span className="font-semibold">{count}</span>
        </Link>
      )}

      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-black/90 backdrop-blur-lg py-3 shadow-lg shadow-black/50' : 'bg-gradient-to-b from-black/80 to-transparent py-5'}`}>
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3 group">
            <img
              src="/assets/logos/Phoelynx_Transparent.webp"
              alt="Phoelynx France"
              className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 object-contain transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 group-hover:brightness-110 drop-shadow-lg group-hover:drop-shadow-xl"
            />
            <span className="lg:text-2xl md:text-xl sm:text-lg font-bold bg-gradient-to-r from-blue-400 via-white to-red-400 bg-clip-text text-transparent">
              Phoelynx France
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center lg:space-x-8 md:space-x-4">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-1 lg:space-x-2 lg:px-4 md:px-2 py-2 rounded-lg transition-all hover:bg-white/10 ${
                    item.path === '/boutique' ? 'bg-blue-500/20 text-blue-300' : 'text-gray-300 hover:text-white'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="lg:text-base md:text-sm">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden mt-4 pb-4 border-t border-white/10">
            <div className="flex flex-col space-y-2 pt-4">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all hover:bg-white/10 ${
                      item.path === '/boutique' ? 'bg-blue-500/20 text-blue-300' : 'text-gray-300 hover:text-white'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </nav>
        )}
      </nav>

      {/* Header */}
      <section className="pt-32 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>

          <div className="flex items-center gap-4 mb-6">
            <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-red-600/20 to-sky-400/20">
              <ShoppingCart className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold">BOUTIQUE</h1>
          </div>

          <p className="text-gray-400 text-lg mb-8">
            Matériel informatique, électronique et robotique de qualité professionnelle
          </p>

          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un produit..."
                className="w-full pl-10 pr-4 py-3 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-red-500 focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-3 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:border-red-500 focus:outline-none"
              >
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
              <span className="ml-4 text-gray-400">Chargement des produits...</span>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="mx-auto h-16 w-16 text-gray-500 mb-4" />
              <h3 className="text-xl font-medium text-gray-400 mb-2">Aucun produit disponible</h3>
              <p className="text-gray-500">
                {selectedCategory === 'Tous' 
                  ? "Aucun produit n'est disponible pour le moment." 
                  : `Aucun produit dans la catégorie "${selectedCategory}".`}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts.map((product) => (
              <div
                key={product.id}
                className="group bg-gray-900/50 rounded-xl overflow-hidden border border-gray-800 hover:border-red-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-red-500/10"
                onMouseEnter={() => setHoveredProduct(product.id)}
                onMouseLeave={() => setHoveredProduct(null)}
              >
                <div className="aspect-square bg-gradient-to-br from-red-600/10 to-sky-400/10 flex items-center justify-center relative overflow-hidden">
                  {product.imageUrl ? (
                    <img 
                      src={product.imageUrl} 
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <ShoppingCart className={`w-16 h-16 text-red-400/30 transition-all duration-300 ${hoveredProduct === product.id ? 'scale-110' : ''}`} />
                  )}

                  {product.popular && (
                    <div className="absolute top-3 right-3">
                      <span className="text-xs px-2 py-1 bg-yellow-600/20 text-yellow-400 rounded-full flex items-center gap-1">
                        <Star className="w-3 h-3" /> Populaire
                      </span>
                    </div>
                  )}
                </div>

                <div className="p-6">
                  {(() => {
                    const inStock = (product.quantity ?? 0) > 0;
                    const purchasable = product.priceAmount != null && inStock && product.available;
                    return (
                      <>
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-xs px-3 py-1 bg-red-600/20 text-red-400 rounded-full">
                            {product.category}
                          </span>
                          {!inStock && (
                            <span className="text-xs px-2 py-1 bg-red-600/20 text-red-400 rounded-full">
                              Rupture
                            </span>
                          )}
                        </div>

                        <h3 className="font-bold text-lg mb-2 group-hover:text-red-400 transition-colors">
                          {product.name}
                        </h3>

                        <p className="text-gray-400 text-sm mb-4">
                          {product.description}
                        </p>

                        <div className="flex justify-between items-center">
                          <span className="text-2xl font-bold text-red-400">{product.price}</span>
                          <button
                            onClick={() => purchasable && add({
                              productId: product.id,
                              name: product.name,
                              priceAmount: product.priceAmount!,
                              imageUrl: product.imageUrl,
                              maxQuantity: product.quantity ?? 1,
                            })}
                            disabled={!purchasable}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                              purchasable
                                ? 'bg-red-600 hover:bg-red-700 text-white transform hover:scale-105'
                                : 'bg-gray-800 text-gray-500 cursor-not-allowed'
                            }`}
                          >
                            {!inStock ? 'Rupture' : !purchasable ? 'Indisponible' : 'Ajouter au panier'}
                          </button>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
              ))}
            </div>
          )}

          {/* Newsletter Section */}
          <div className="mt-16 text-center p-8 border border-gray-800 rounded-2xl bg-gray-900/20">
            <h3 className="text-2xl font-bold mb-4">Restez informé des nouveautés</h3>
            <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
              Inscrivez-vous à notre newsletter pour être alerté des nouveaux produits,
              des promotions et des formations disponibles.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Votre email"
                className="flex-1 px-4 py-3 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-red-500 focus:outline-none"
              />
              <button className="px-6 py-3 bg-gradient-to-r from-red-600 to-sky-400 text-white font-semibold rounded-lg hover:shadow-lg hover:shadow-red-500/25 transition-all">
                S'inscrire
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center space-x-3">
              <img
                src="/assets/logos/Phoelynx_Transparent.webp"
                alt="Phoelynx France"
                className="w-8 h-8 object-contain"
              />
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-400 via-white to-red-400 bg-clip-text text-transparent">
                Phoelynx France
              </span>
            </div>

            <div className="flex items-center gap-6">
              <Globe className="w-5 h-5 text-gray-400 hover:text-red-400 cursor-pointer transition-colors" />
              <span className="text-gray-400">phoelynx.fr</span>
            </div>

            <div className="flex flex-col items-center md:items-end gap-2">
              <p className="text-gray-400 text-sm">© 2025-{currentYear} Phoelynx France. Tous droits réservés.</p>
              <div className="flex gap-4 text-xs">
                <Link to="/mentions-legales" className="text-gray-500 hover:text-red-400 transition-colors">
                  Mentions légales
                </Link>
                <Link to="/politique-confidentialite" className="text-gray-500 hover:text-red-400 transition-colors">
                  Confidentialité
                </Link>
                <Link to="/conditions-utilisation" className="text-gray-500 hover:text-red-400 transition-colors">
                  CGU
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BoutiquePage;